import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useSearchParams, Link, useParams, useNavigate } from "react-router-dom";
import { FolderTree, Plus, X, ChevronDown } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/SearchBar";
import CategoryTree from "@/components/CategoryTree";
import Pagination from "@/components/Pagination";
import { useQuote } from "@/context/QuoteContext";
import { toast } from "sonner";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import Seo from "@/components/Seo";
import { productPath, categoryPath, breadcrumbLd } from "@/lib/seo";

const PAGE_SIZE = 40;
const SECTOR_PREVIEW = 12;

export default function CatalogPage() {
  const [params, setParams] = useSearchParams();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [facets, setFacets] = useState({ markalar: [] });
  const [tree, setTree] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [usecases, setUsecases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sectAll, setSectAll] = useState(false);
  const [openSections, setOpenSections] = useState({ kat: true, sek: true, mar: true });
  const { addItem } = useQuote();
  const reqRef = useRef(0);
  const listTopRef = useRef(null);

  const q = params.get("q") || "";
  const marka = params.get("marka") || "";
  const slugCat = slug ? tree.find((c) => c.seo_slug === slug) : null;
  const kategori = slug ? (slugCat?.kategori_id || "") : (params.get("kategori") || "");
  const sektor = params.get("sektor") || "";
  const kullanim = params.get("kullanim") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);

  useEffect(() => {
    api.get("/catalog/facets").then((r) => setFacets(r.data)).catch(() => {});
    api.get("/catalog/category-tree").then((r) => setTree(r.data.items || [])).catch(() => {});
    api.get("/catalog/sectors").then((r) => setSectors(r.data.items || [])).catch(() => {});
    api.get("/catalog/usecases").then((r) => setUsecases(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (slug && tree.length && !slugCat) {
      api.get(`/redirects/resolve?path=${encodeURIComponent(`/kategori/${slug}`)}`)
        .then((r) => { if (r.data.yeni) navigate(r.data.yeni, { replace: true }); }).catch(() => {});
    }
  }, [slug, tree, slugCat]); // eslint-disable-line react-hooks/exhaustive-deps


  const load = useCallback(() => {
    if (slug && !slugCat) return; // kategori ağacı yüklenene kadar bekle
    setLoading(true);
    const id = ++reqRef.current;
    const qs = new URLSearchParams({ q, marka, kategori, sektor, kullanim, page, page_size: PAGE_SIZE });
    api.get(`/catalog/products?${qs}`)
      .then((r) => {
        if (id !== reqRef.current) return; // gecikmiş eski cevabı yok say
        setData(r.data);
      })
      .catch(() => {})
      .finally(() => { if (id === reqRef.current) setLoading(false); });
  }, [q, marka, kategori, sektor, kullanim, page, slug, slugCat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Eski ?kategori=ID adreslerini anlamlı kategori adresine taşı
  useEffect(() => {
    const legacy = params.get("kategori");
    if (slug || !legacy || !tree.length) return;
    const c = tree.find((x) => x.kategori_id === legacy);
    if (c?.seo_slug) navigate(`/kategori/${c.seo_slug}${page > 1 ? `?page=${page}` : ""}`, { replace: true });
  }, [slug, params, tree]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const scrollToListTop = () => {
    requestAnimationFrame(() => {
      if (listTopRef.current) {
        const top = listTopRef.current.getBoundingClientRect().top + window.scrollY - 130;
        window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      }
    });
  };

  // Filtre değişimi: aynı anda yalnızca bir filtre aktif olabilir.
  // Kategori/marka/sektör/kullanım birbirini temizler; kategori seçimi eski aramayı da temizler.
  const setFilter = (key, val) => {
    const p = new URLSearchParams();
    if (key !== "kategori" && q) p.set("q", q);
    if (key === "kategori") {
      const c = tree.find((x) => x.kategori_id === val);
      navigate(val && c ? categoryPath(c) : "/urunler");
      return;
    }
    if (val) p.set(key, val);
    p.set("page", "1");
    navigate(`/urunler?${p}`);
  };
  const setSearch = (val) => {
    const p = new URLSearchParams(params);
    if (val) p.set("q", val); else p.delete("q");
    p.set("page", "1");
    setParams(p);
  };
  const setPage = (n) => {
    const p = new URLSearchParams(params);
    if (n > 1) p.set("page", String(n)); else p.delete("page");
    setParams(p);
    scrollToListTop();
  };
  const clearFilters = () => navigate(q ? `/urunler?q=${encodeURIComponent(q)}` : "/urunler");
  const clearAll = () => navigate("/urunler");

  const add = (p) => { addItem(p); toast.success(`${p.urun_adi_tr} teklif sepetine eklendi`); };

  const byId = useMemo(() => {
    const m = {};
    tree.forEach((c) => { m[c.kategori_id] = c; });
    return m;
  }, [tree]);

  const crumbs = useMemo(() => {
    if (!kategori) return [];
    const out = [];
    let cur = byId[kategori];
    const seen = new Set();
    while (cur && !seen.has(cur.kategori_id)) {
      seen.add(cur.kategori_id);
      out.unshift(cur);
      cur = byId[cur.ust_kategori_id];
    }
    return out;
  }, [kategori, byId]);

  const subcats = useMemo(
    () => (kategori ? tree.filter((c) => c.ust_kategori_id === kategori) : []),
    [kategori, tree]
  );

  const sektorAdi = useMemo(
    () => sectors.find((s) => s.sektor_id === sektor)?.sektor_adi_tr,
    [sectors, sektor]
  );
  const kullanimAdi = useMemo(
    () => usecases.find((u) => u.kullanim_id === kullanim)?.kullanim_adi_tr,
    [usecases, kullanim]
  );

  const activeTags = useMemo(() => {
    const t = [];
    if (q) t.push({ key: "q", label: `Arama: ${q}` });
    if (kategori) t.push({ key: "kategori", label: `Kategori: ${byId[kategori]?.kategori_adi || kategori}` });
    if (sektor) t.push({ key: "sektor", label: `Sektör: ${sektorAdi || sektor}` });
    if (kullanim) t.push({ key: "kullanim", label: `Kullanım: ${kullanimAdi || kullanim}` });
    if (marka) t.push({ key: "marka", label: `Marka: ${marka}` });
    return t;
  }, [q, kategori, sektor, kullanim, marka, byId, sektorAdi, kullanimAdi]);

  const removeTag = (key) => {
    if (key === "q") setSearch("");
    else setFilter(key, "");
  };

  const toggleSection = (k) => setOpenSections((s) => ({ ...s, [k]: !s[k] }));

  const SectionHeader = ({ title, k, icon: Icon }) => (
    <button onClick={() => toggleSection(k)} data-testid={`section-${k}`}
      className="w-full flex items-center justify-between font-head font-semibold text-sm text-slate-900 mb-2">
      <span className="flex items-center gap-1.5">{Icon && <Icon className="w-4 h-4 text-teal-600" />}{title}</span>
      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${openSections[k] ? "rotate-180" : ""}`} />
    </button>
  );

  const visibleSectors = sectAll ? sectors : sectors.slice(0, SECTOR_PREVIEW);

  const Filters = () => (
    <div className="space-y-5">
      <div>
        <SectionHeader title="Kategoriler" k="kat" icon={FolderTree} />
        {openSections.kat && (
          <CategoryTree items={tree} selected={kategori}
            onSelect={(id) => { setFilter("kategori", id); setMobileOpen(false); }} />
        )}
      </div>
      <div>
        <SectionHeader title="Sektörler" k="sek" />
        {openSections.sek && (
          <div className="space-y-1">
            {visibleSectors.map((s) => (
              <button key={s.sektor_id} data-testid={`filter-sektor-${s.sektor_id}`}
                onClick={() => setFilter("sektor", sektor === s.sektor_id ? "" : s.sektor_id)}
                className={`block w-full text-left text-sm px-2 py-1.5 rounded-md transition-colors leading-snug ${sektor === s.sektor_id ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {s.sektor_adi_tr}
              </button>
            ))}
            {sectors.length > SECTOR_PREVIEW && (
              <button onClick={() => setSectAll(!sectAll)} data-testid="toggle-all-sectors"
                className="text-xs text-teal-600 hover:underline px-2 py-1">
                {sectAll ? "Daha az göster" : `Tüm sektörleri göster (${sectors.length})`}
              </button>
            )}
          </div>
        )}
      </div>
      <div>
        <SectionHeader title="Tedarikçi Markası / Marka" k="mar" />
        {openSections.mar && (
          <div className="space-y-1">
            {facets.markalar.map((it) => (
              <button key={it} data-testid={`filter-marka-${it}`}
                onClick={() => setFilter("marka", marka === it ? "" : it)}
                className={`block w-full text-left text-sm px-2 py-1.5 rounded-md transition-colors ${marka === it ? "bg-teal-600 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
                {it}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const filtered = !!(q || marka || sektor || kullanim);
  const pageSfx = page > 1 ? `?page=${page}` : "";
  const outOfRange = page > 1 && data && page > (data.pages || 1);
  const hrefFor = (n) => { const p = new URLSearchParams(params); if (n > 1) p.set("page", String(n)); else p.delete("page"); const s = p.toString(); return window.location.pathname + (s ? `?${s}` : ""); };
  const curCat = crumbs.length ? crumbs[crumbs.length - 1] : null;
  const seoEl = curCat ? (
    <Seo title={page > 1 ? (curCat.seo_title || `${curCat.kategori_adi} | Molar Kimya`).replace(/ \| Molar Kimya$/, "") + ` – Sayfa ${page} | Molar Kimya` : (curCat.seo_title || `${curCat.kategori_adi} | Molar Kimya`)}
      description={curCat.seo_description} path={categoryPath(curCat) + pageSfx} noindex={filtered || outOfRange || curCat.indeksle === false} status={outOfRange ? 404 : undefined}
      jsonLd={[breadcrumbLd([{ name: "Ürünler", path: "/urunler" },
        ...crumbs.map((c) => ({ name: c.kategori_adi, path: categoryPath(c) }))])]} />
  ) : (
    <Seo title={`Ürün Kataloğu${page > 1 ? ` – Sayfa ${page}` : ""} | Molar Kimya`}
      description="Laboratuvar kimyasalları, cihazlar, sarf malzemeleri ve gıda hammaddeleri kataloğu. Ürünleri inceleyin, teklif listenize ekleyin."
      path={`/urunler${filtered ? "" : pageSfx}`} noindex={filtered || outOfRange} status={outOfRange ? 404 : undefined} />
  );

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-8 w-full">
      {seoEl}
      <div className="mb-4" ref={listTopRef}>
        <SearchBar value={q} onSearch={setSearch}
          scopeLabel={crumbs.length ? crumbs[crumbs.length - 1].kategori_adi : ""} />
      </div>

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <h1 className="font-head text-xl font-semibold text-slate-900" data-testid="catalog-h1">
          {curCat ? curCat.kategori_adi : "Ürün Kataloğu"} {data.total > 0 && <span className="text-slate-400 font-normal text-base">({data.total.toLocaleString("tr-TR")} ürün)</span>}
        </h1>
        <Button variant="outline" size="sm" className="lg:hidden ml-auto" onClick={() => setMobileOpen(true)}
          data-testid="mobile-categories-btn">
          <FolderTree className="w-4 h-4 mr-1.5" /> Kategoriler ve Filtreler {activeTags.length > 0 && `(${activeTags.length})`}
        </Button>
        {activeTags.length > 0 && (
          <button onClick={clearAll} data-testid="clear-filters-btn"
            className="text-sm text-slate-500 hover:text-red-500 flex items-center gap-1">
            <X className="w-3.5 h-3.5" /> Tüm filtreleri temizle
          </button>
        )}
      </div>

      {/* Aktif filtre etiketleri */}
      {activeTags.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="active-filters">
          {activeTags.map((t) => (
            <span key={t.key} data-testid={`filter-tag-${t.key}`}
              className="inline-flex items-center gap-1.5 bg-teal-50 border border-teal-200 text-teal-800 text-xs font-medium rounded-full pl-3 pr-1.5 py-1">
              {t.label}
              <button onClick={() => removeTag(t.key)} data-testid={`filter-tag-remove-${t.key}`}
                aria-label={`${t.label} filtresini kaldır`}
                className="rounded-full p-0.5 hover:bg-teal-100">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Tıklanabilir kategori yolu */}
      {crumbs.length > 0 && (
        <nav className="flex items-center flex-wrap gap-1 text-sm mb-4" data-testid="category-breadcrumb">
          <button onClick={() => setFilter("kategori", "")} data-testid="crumb-all"
            className="text-slate-500 hover:text-teal-600">Tüm Ürünler</button>
          {crumbs.map((c) => (
            <React.Fragment key={c.kategori_id}>
              <span className="text-slate-300">›</span>
              <button onClick={() => setFilter("kategori", c.kategori_id)}
                data-testid={`crumb-${c.kategori_id}`}
                className={c.kategori_id === kategori ? "text-teal-700 font-semibold" : "text-slate-500 hover:text-teal-600"}>
                {c.kategori_adi}
              </button>
            </React.Fragment>
          ))}
        </nav>
      )}

      {outOfRange && (
        <div className="bg-white border rounded-xl p-6 mb-5 text-center" data-testid="page-out-of-range">
          <p className="font-semibold text-slate-900">Bu sayfa bulunamadı</p>
          <p className="text-sm text-slate-500 mt-1">Katalogda {data.pages || 1} sayfa bulunuyor.</p>
          <a href={hrefFor(1)} onClick={(e) => { e.preventDefault(); setPage(1); }} className="inline-block mt-3 text-sm text-teal-700 underline">İlk sayfaya dön</a>
        </div>
      )}

      {/* Alt kategori bağlantıları */}
      {subcats.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="subcategory-links">
          {subcats.map((c) => (
            <button key={c.kategori_id} onClick={() => setFilter("kategori", c.kategori_id)}
              data-testid={`subcat-${c.kategori_id}`}
              className="px-3.5 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 hover:border-teal-400 hover:text-teal-700 transition-colors">
              {c.kategori_adi}
              <span className="ml-1.5 text-xs text-slate-400">{c.toplam_urun_sayisi}</span>
            </button>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-[290px_minmax(0,1fr)] gap-8 items-start">
        <aside className="hidden lg:block bg-white border border-slate-200 rounded-xl p-5 lg:sticky lg:top-28 max-h-[calc(100vh-8.5rem)] overflow-y-auto">
          <Filters />
        </aside>

        <div className="min-w-0">
          {!loading && data.items.length > 0 && (
            <div className="mb-4">
              <Pagination page={page} pages={data.pages} total={data.total} pageSize={PAGE_SIZE}
                onChange={setPage} hrefFor={hrefFor} position="top" />
            </div>
          )}

          {loading ? (
            <div className="space-y-2" data-testid="loading-skeleton">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="h-12 bg-white border border-slate-200 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-16 bg-white border border-slate-200 rounded-xl" data-testid="no-products">
              <p className="text-slate-600 font-medium">Seçili arama ve filtrelerle eşleşen ürün bulunamadı.</p>
              <p className="text-sm text-slate-400 mt-1">Arama veya filtreleri temizleyerek devam edebilirsiniz.</p>
              <div className="flex gap-3 justify-center mt-5">
                {q && (
                  <Button variant="outline" onClick={() => setSearch("")} data-testid="empty-clear-search">
                    Aramayı temizle
                  </Button>
                )}
                <Button variant="outline" onClick={clearFilters} data-testid="empty-clear-filters">
                  Diğer filtreleri temizle
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto rounded-lg border border-slate-200 shadow-sm bg-white">
                <table className="excel-table w-full text-sm" data-testid="products-table">
                  <thead>
                    <tr>
                      <th className="text-left px-3 py-2.5 whitespace-nowrap">Ürün Kodu</th>
                      <th className="text-left px-3 py-2.5 min-w-[220px]">Ürün Adı</th>
                      <th className="text-left px-3 py-2.5 w-[130px] leading-tight" data-testid="th-marka">Tedarikçi Markası / Marka</th>
                      <th className="text-left px-3 py-2.5 whitespace-nowrap">Ambalaj</th>
                      <th className="text-right px-3 py-2.5 whitespace-nowrap">İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((p) => (
                      <tr key={p.urun_id} data-testid={`product-row-${p.urun_id}`}>
                        <td className="px-3 py-2.5 font-mono text-xs text-slate-600 whitespace-nowrap" data-nosnippet>{p.urun_kodu}</td>
                        <td className="px-3 py-2.5">
                          <Link to={productPath(p)} className="text-slate-900 hover:text-teal-700 font-medium">
                            {p.urun_adi_tr}
                          </Link>
                          {p.cas_no && <span className="ml-2 text-xs text-slate-500 font-mono whitespace-nowrap">CAS {p.cas_no}</span>}
                          {p.kisa_aciklama && <div className="text-xs text-slate-600 mt-0.5">{p.kisa_aciklama}</div>}
                        </td>
                        <td className="px-3 py-2.5 text-slate-700 text-xs leading-snug">{p.marka}</td>
                        <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{p.ambalaj || "—"}</td>
                        <td className="px-3 py-2.5 text-right">
                          <Button size="sm" variant="outline" className="h-8 border-teal-200 text-teal-700 hover:bg-teal-50 whitespace-nowrap"
                            onClick={() => add(p)} data-testid={`add-to-quote-${p.urun_id}`}>
                            <Plus className="w-3.5 h-3.5 mr-1" /> Teklif Sepetine Ekle
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
                {data.items.map((p) => (
                  <div key={p.urun_id} className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm"
                    data-testid={`product-card-${p.urun_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-xs text-slate-500" data-nosnippet>Ürün kodu: {p.urun_kodu}</span>
                      {p.cas_no && <span className="font-mono text-[10px] text-slate-500">CAS {p.cas_no}</span>}
                    </div>
                    <Link to={productPath(p)} className="font-medium text-slate-900 block mt-1 leading-snug break-words">{p.urun_adi_tr}</Link>
                    <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                      <div><span className="text-slate-500">Tedarikçi Markası / Marka:</span> {p.marka}</div>
                      <div>{p.ambalaj ? `Ambalaj: ${p.ambalaj}` : ""}{p.e_kodu_bilgisi ? ` · ${p.e_kodu_bilgisi}` : ""}</div>
                    </div>
                    {p.kisa_aciklama && (
                      <ExpandableDesc text={p.kisa_aciklama} />
                    )}
                    <Link to={productPath(p)} className="text-xs text-teal-700 hover:underline mt-2 inline-block" data-testid={`detail-link-${p.urun_id}`}>
                      Ürün detayı →
                    </Link>
                    <Button size="sm" className="w-full mt-2 bg-teal-600 hover:bg-teal-700 whitespace-nowrap" onClick={() => add(p)}
                      data-testid={`add-to-quote-m-${p.urun_id}`}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Teklif Sepetine Ekle
                    </Button>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Pagination page={page} pages={data.pages} total={data.total} pageSize={PAGE_SIZE}
                  onChange={setPage} hrefFor={hrefFor} position="bottom" />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobil kategori + filtre paneli */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[320px] max-w-[85vw] overflow-y-auto flex flex-col">
          <SheetHeader><SheetTitle className="font-head">Kategoriler ve Filtreler</SheetTitle></SheetHeader>
          {activeTags.length > 0 && (
            <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
              <span>{data.total.toLocaleString("tr-TR")} ürün · {activeTags.length} filtre</span>
              <button onClick={() => { clearAll(); }} data-testid="mobile-clear-filters" className="text-red-500">Filtreleri temizle</button>
            </div>
          )}
          <div className="mt-3 flex-1"><Filters /></div>
          <Button className="mt-4 bg-teal-600 hover:bg-teal-700 h-11 sticky bottom-0" onClick={() => setMobileOpen(false)}
            data-testid="mobile-show-results">
            Sonuçları Göster ({data.total.toLocaleString("tr-TR")})
          </Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function ExpandableDesc({ text }) {
  const [open, setOpen] = useState(false);
  const long = text.length > 90;
  return (
    <div className="mt-1.5">
      <p className={`text-xs text-slate-600 leading-snug ${open ? "" : "line-clamp-2"}`}>{text}</p>
      {long && (
        <button onClick={() => setOpen(!open)} data-testid="toggle-desc"
          className="text-xs text-teal-700 hover:underline mt-0.5">
          {open ? "Daha az" : "Devamını gör"}
        </button>
      )}
    </div>
  );
}

